<template>
  <div>
    <h3>All Faculty</h3>
    <span>This is a list of all faculty who require a workload. Their latest workload information is included.</span>

    <b-form inline @submit.prevent="" class="mb-2">
      <b-input-group class="filter-group">
        <b-input-group-prepend is-text>
          <font-awesome-icon icon="search" />
        </b-input-group-prepend>
        <b-form-input v-model="filter" placeholder="Type to Search" debounce="500"></b-form-input>
        <b-input-group-append>
          <b-button @click="clearFilter()">Clear</b-button>
        </b-input-group-append>
      </b-input-group>

      <b-input-group class="filter-group">
        <b-form-select v-model="selectedTimePeriodId"
                      :options="timePeriods"
                      value-field="timePeriodId"
                      text-field="description">
          <template slot="first">
            <!-- this slot appears above the options from 'options' prop -->
            <option :value="null">All Years</option>
          </template>
        </b-form-select>
      </b-input-group>

      <b-input-group class="filter-group">
        <b-input-group-prepend is-text>
          Courses
        </b-input-group-prepend>
        <b-form-input v-model="prefix" placeholder="ex: EXW, KIN, NTR"></b-form-input>
      </b-input-group>

      <b-input-group class="filter-group">
        <b-form-checkbox @input="filterItems()" v-model="chsOnly" class="font-weight-bold">CHS Only</b-form-checkbox>
      </b-input-group>
    </b-form>

    <b-table striped
             hover
             :items="filteredItems"
             :fields="fields"
             :per-page="perPage"
             :current-page="currentPage"
             :sort-compare="customSort"
             sort-by="name"
             responsive
             show-empty>
      <template v-slot:cell(name)="data">
        {{ data.item.lastName }}, {{ data.item.firstName }}
      </template>
      <template v-slot:cell(jobcodeDescr)="data">
        {{ getPrimaryEmployeeJob(data.item.employeeJobs) ? getPrimaryEmployeeJob(data.item.employeeJobs).job.description : "" }}
      </template>
      <template v-for="workloadTypeDefinition in workloadTypeDefinitions" v-slot:[dynamicCellName(workloadTypeDefinition.name)]="data">
        {{ ( data.item.latestWorkload && getWorkloadType(data.item.latestWorkload.workloadTypes, workloadTypeDefinition.workloadTypeDefinitionId) ) ?
            getWorkloadType(data.item.latestWorkload.workloadTypes, workloadTypeDefinition.workloadTypeDefinitionId).value : null | percentage(0)}}
      </template>
      <template v-slot:cell(status)="data">
        {{ data.item.latestWorkload ? data.item.latestWorkload.currentStatus.workloadStatusDefinition.name : null }}
      </template>
      <template v-slot:cell(timePeriod)="data">
        {{ (data.item.latestWorkload ? data.item.latestWorkload.timePeriod.description : null ) }}
      </template>

      <!-- <template v-slot:cell(fallPlanned)="data">
        <div v-for="(course, index) in termCourses(data.item.latestWorkload, 'fall')" :key="index">
          {{ course.courseTitle }} - {{ course.units }}
        </div>
      </template>

      <template v-slot:cell(springPlanned)="data">
        <div v-for="(course, index) in termCourses(data.item.latestWorkload, 'spring')" :key="index">
          {{ course.courseTitle }} - {{ course.units }}
        </div>
      </template>

      <template v-slot:cell(summerPlanned)="data">
        <div v-for="(course, index) in termCourses(data.item.latestWorkload, 'summer')" :key="index">
          {{ course.courseTitle }} - {{ course.units }}
        </div>
      </template>

      <template v-slot:cell(fallActual)="data">
        <combined-courses :courses="termClasses(data.item.classRecords, 'fall')"/>
      </template>

      <template v-slot:cell(springActual)="data">
        <combined-courses :courses="termClasses(data.item.classRecords, 'spring')"/>
      </template>

      <template v-slot:cell(summerActual)="data">
        <combined-courses :courses="termClasses(data.item.classRecords, 'summer')"/>
      </template> -->

      <template v-slot:cell(courses)="data">
        <b-button size="sm" @click="data.toggleDetails" class="mr-2" v-if="data.item.latestWorkload">
          {{ data.detailsShowing ? 'Hide' : 'Show'}} Details
        </b-button>
      </template>

      <template v-slot:row-details="data">
        <b-card class="mt-3">
          <h4>Planned Courses</h4>
          <b-row>
            <b-col sm="4" v-for="timePeriodTerm in data.item.latestWorkload.timePeriod.timePeriodTerms" :key="timePeriodTerm.termId">
              <b-card class="my-1 workload-term-card">
                <h5 v-if="timePeriodTerm.term">{{ timePeriodTerm.term.description }}</h5>

                <b-table hover
                        small
                        :fields="courseFields"
                        :items="termCourses(data.item.latestWorkload, timePeriodTerm.term.termCode)"
                        class="table-plain"
                        >
                  <template v-slot:cell(contributesToTeaching)="data">
                    <font-awesome-icon icon="minus-circle"
                                      v-b-tooltip.hover
                                      title="This course does not contribute to Teaching %."
                                      v-if="!data.item.contributesToTeaching"></font-awesome-icon>
                  </template>
                  <template v-slot:cell(new)="data">
                    <font-awesome-icon icon="exclamation-triangle"
                                      v-b-tooltip.hover
                                      title="This course does not match any courses currently in the database."
                                      v-if="data.item.new"></font-awesome-icon>
                  </template>
                  <template v-slot:cell(course)="data">
                    {{data.item.subjectCode}} {{data.item.catalogNumber}} - {{data.item.courseTitle}}
                  </template>
                  <template v-slot:cell(notes)="data">
                    <div class="text-right" v-if="data.item.notes">
                      <font-awesome-icon icon="sticky-note"
                                        v-b-tooltip.hover
                                        :title="data.item.notes"></font-awesome-icon>
                    </div>
                  </template>
                </b-table>
              </b-card>
            </b-col>
          </b-row>
        </b-card>
        <b-card class="mt-3" v-if="data.item.classRecords && data.item.classRecords.length">
          <h4>Actual Courses</h4>
          <b-alert variant="info" show>These are the courses from PeopleSoft that were actually taught. Courses grouped with the <font-awesome-icon icon="link" /> icon are combined courses. </b-alert>
          <b-row>
            <b-col sm="4" v-for="timePeriodTerm in data.item.latestWorkload.timePeriod.timePeriodTerms" :key="timePeriodTerm.term.termCode">
              <b-card class="my-1 workload-term-card">
                <h5 v-if="timePeriodTerm.term">{{ timePeriodTerm.term.description }}</h5>

                <combined-courses :courses="termClassRecords(data.item.classRecords, timePeriodTerm.term.termCode)" type="table-plain"/>
                
              </b-card>
            </b-col>
          </b-row>
        </b-card>
      </template>
      <!-- <template v-slot:cell(details)="data">
        <router-link :to="{ name: 'workload', params: { workloadId: data.item.workloadId } }">Details</router-link>
      </template> -->
    </b-table>

    <b-pagination :total-rows="totalRows" v-model="currentPage" :per-page="perPage" align="right" />
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import CombinedCourses from "../../components/workloads/combined-courses";

export default {
  data() {
    return {
      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      filter: "",
      selectedTimePeriodId: null,
      chsOnly: true,
      prefix: "",
      courseFields: [
        { key: "new", label: "" },
        { key: "contributesToTeaching", label: "" },
        { key: "course" },
        { key: "units" },
        { key: "notes", label: "" }
      ]
    };
  },

  components: {
    CombinedCourses
  },

  computed: {
    ...mapState({
      timePeriods: state => state.timePeriods.timePeriods,
      nextTimePeriod: state => state.status.nextTimePeriod,
      workloadFaculty: state => state.employees.workloadFaculty,
      workloadTypeDefinitions: state => state.workloads.workloadTypeDefinitions
    }),

    fields: function() {
      var tableFields = [];

      if (this.showDiscrepancyCol) {
        tableFields.push({ key: "discrepancy", label: "" });
      }

      tableFields.push({ key: "name", sortable: true });
      tableFields.push({ key: "asuriteId", sortable: true });
      tableFields.push({ key: "jobcodeDescr", sortable: true });
      tableFields.push({ key: "status", sortable: true });
      tableFields.push({ key: "timePeriod", sortable: true });
      tableFields.push({ key: "notes", sortable: true });

      _.forEach(this.workloadTypeDefinitions, function(workloadTypeDefinition) {
        var newField = {
          key: workloadTypeDefinition.name,
          label: workloadTypeDefinition.name + " (%)",
          sortable: true
        };
        tableFields.push(newField);
      });
      tableFields.push({ key: "courses" });
      // tableFields.push({ key: "fallPlanned" });
      // tableFields.push({ key: "springPlanned" });
      // tableFields.push({ key: "summerPlanned" });

      // tableFields.push({ key: "fallActual" });
      // tableFields.push({ key: "springActual" });
      // tableFields.push({ key: "summerActual" });

      tableFields.push({ key: "details", label: "" });

      return tableFields;
    },

    filteredItems: function() {
      var filtered = this.workloadFaculty;

      if (this.chsOnly) {
        filtered = this.matchChs(filtered);
      }

      if (this.selectedTimePeriodId) {
        filtered = this.matchTimePeriod(filtered, this.selectedTimePeriodId);
      }

      if (this.prefix) {
        filtered = this.matchCoursePrefix(filtered, this.prefix);
      }

      filtered = this.search(filtered, this.filter);

      return filtered;
    }
  },

  watch: {
    // filter(newFilter) {
    //   if (newFilter != null) {
    //     localStorage.filter = newFilter;
    //   }
    // },
    // chsOnly(newValue) {
    //   if (newValue != null) {
    //     localStorage.chsOnly = newValue;
    //   }
    // },
    filteredItems(items) {
      this.totalRows = items.length;
      this.currentPage = 1;
    }
  },

  methods: {
    ...mapActions("employees", ["getWorkloadFaculty"]),

    ...mapActions("timePeriods", ["getTimePeriods"]),

    ...mapActions("status", ["getNextTimePeriod"]),

    ...mapActions("workloads", ["getWorkloadTypeDefinitions"]),

    dynamicCellName(name) {
      return "cell(" + name + ")";
    },

    getWorkloadType(workloadTypes, workloadTypeDefinitionId) {
      return _.find(workloadTypes, function(workloadType) {
        return (
          workloadType.workloadTypeDefinitionId === workloadTypeDefinitionId
        );
      });
    },

    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    // a and b are two record objects being compared, key is the key of the field being sorted on
    // return null to use the default sort-compare routine
    customSort(a, b, key) {
      if (key === "name") {
        var nameA = a.lastName + ", " + a.firstName;
        var nameB = b.lastName + ", " + b.firstName;
        return nameA.localeCompare(nameB);
      }
      if (key === "jobcodeDescr") {
        var jobcodeDescrA = this.getPrimaryEmployeeJob(a.employeeJobs)
          ? this.getPrimaryEmployeeJob(a.employeeJobs).job.description
          : "";
        var jobcodeDescrB = this.getPrimaryEmployeeJob(b.employeeJobs)
          ? this.getPrimaryEmployeeJob(b.employeeJobs).job.description
          : "";
        return jobcodeDescrA.localeCompare(jobcodeDescrB);
      }
      if (key === "status") {
        var statusNameA = a.latestWorkload
          ? a.latestWorkload.currentStatus.workloadStatusDefinition.name
          : "";
        var statusNameB = b.latestWorkload
          ? b.latestWorkload.currentStatus.workloadStatusDefinition.name
          : "";
        return statusNameA.localeCompare(statusNameB);
      }

      if (key === "timePeriod") {
        var timePeriodA = a.latestWorkload
          ? a.latestWorkload.timePeriod.description
          : "";
        var timePeriodB = b.latestWorkload
          ? b.latestWorkload.timePeriod.description
          : "";
        return timePeriodA.localeCompare(timePeriodB);
      }
      // try to find the key in the workload type definitions
      var workloadTypeDefinition = _.find(
        this.workloadTypeDefinitions,
        function(workloadTypeDefinition) {
          return workloadTypeDefinition.name === key;
        }
      );
      if (workloadTypeDefinition) {
        // get the workload type object for a and b for this key
        var workloadTypeA = a.latestWorkload
          ? this.getWorkloadType(
              a.latestWorkload.workloadTypes,
              workloadTypeDefinition.workloadTypeDefinitionId
            )
          : null;
        var workloadTypeB = b.latestWorkload
          ? this.getWorkloadType(
              b.latestWorkload.workloadTypes,
              workloadTypeDefinition.workloadTypeDefinitionId
            )
          : null;
        // initialize as 0
        var valA = 0;
        var valB = 0;
        // if a workloadType was returned, set the val as that value
        if (workloadTypeA) {
          valA = workloadTypeA.value;
        }
        if (workloadTypeB) {
          valB = workloadTypeB.value;
        }
        return valA < valB ? -1 : valA > valB ? 1 : 0;
      }
      return null;
    },

    matchChs(collection) {
      return _.filter(collection, function(object) {
        return _.filter(object.employeeJobs, function(job) {
          return job.department.deptId.startsWith("M09");
        }).length;
      });
    },

    matchCoursePrefix(collection, prefixes) {
      if (prefixes) {
        var splits = prefixes.split(",");
        return _.filter(collection, function(object) {
          var hasPrefix = false;
          _.forEach(splits, function(prefix) {
            if (_.includes(object.coursePrefixes, prefix.toUpperCase())) {
              hasPrefix = true;
            }
          });
          return hasPrefix;
        });
      }
    },

    matchTimePeriod(collection, timePeriodId) {
      if (timePeriodId != null) {
        return _.filter(collection, function(object) {
          if (object.latestWorkload) {
            return object.latestWorkload.timePeriodId === timePeriodId;
          }
          return false;
        });
      } else {
        return collection;
      }
    },

    matchFilter(item, filter) {
      if (filter != "" && filter != null) {
        var found = this.search([item], this.filter);
        return found.length > 0;
      }
      return true;
    },

    search(collection, text) {
      var that = this;
      return _.filter(collection, function(object) {
        var search = that.deepSearch(object, text.toLowerCase());
        return _.keys(search).length > 0;
      });
    },

    deepSearch(collection, predicate, thisArg) {
      if (_.isFunction(predicate)) {
        predicate = _.iteratee(predicate, thisArg);
      } else {
        var keys = _.flatten(_.tail(arguments));
        if (typeof keys === "string") {
          keys = keys.toLowerCase();
        }
        predicate = function(val) {
          if (typeof val === "string") {
            val = val.toLowerCase();
          }
          return _.includes(val, keys);
        };
      }
      var that = this;
      return _.transform(collection, function(memo, val, key) {
        var include = predicate(val, key);
        if (!include && _.isObject(val)) {
          val = that.deepSearch(val, predicate);
          include = !_.isEmpty(val);
        }
        if (include) {
          _.isArray(collection) ? memo.push(val) : (memo[key] = val);
        }
      });
    },

    getPrimaryEmployeeJob(employeeJobs) {
      if (!employeeJobs.length) {
        return null;
      }
      var primary = _.find(employeeJobs, function(employeeJob) {
        return employeeJob.jobIndicator === "P";
      });
      if (primary) {
        return primary;
      }
      return employeeJobs[0];
    },

    clearFilter: function() {
      this.filter = "";
      //localStorage.removeItem("filter");
    },

    termCourses: function(workload, term) {
      var courses = [];
      if (workload) {
        courses = _.filter(workload.courses, course => {
          return course.term.termCode == term;
        });
      }

      return courses;
    },

    termClassRecords: function(classes, term) {
      var retClasses = _.filter(classes, c => {
        return c.termCode == term;
      });
      return retClasses;
    }
  },

  created() {
    this.getTimePeriods();

    this.getWorkloadFaculty();

    this.getWorkloadTypeDefinitions();

    // if (localStorage.filter) {
    //   this.filter = localStorage.filter;
    // }

    // if (localStorage.chsOnly) {
    //   this.chsOnly = localStorage.chsOnly === "true";
    // }
  }
};
</script>

<style scoped>
.filter-group {
  margin: 10px 1em 15px 0;
  max-width: 600px;
}
.table-plain tbody tr,
.table-plain tbody tr:hover,
.table-plain tbody td {
  background-color: transparent;
}
.table-plain tbody tr:nth-of-type(odd) {
  background-color: transparent;
}
</style>
